import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { getDashboardOverviewMetrics } from '../../../Actions/DashboardActions';

const MetricCard = ({ title, value, subtitle, change, icon, color, trend }) => {
  const isPositive = change > 0;
  const isNegative = change < 0;

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-xl p-5 flex flex-col gap-3 hover:shadow-lg transition-all duration-300 hover:border-[#0226BE]/20">
      <div className="flex items-center justify-between">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
        {change !== undefined && change !== null && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              isPositive
                ? 'bg-green-50 text-green-600'
                : isNegative
                  ? 'bg-red-50 text-red-600'
                  : 'bg-gray-50 text-gray-600'
            }`}
          >
            {isPositive ? (
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 11l5-5m0 0l5 5m-5-5v12"
                />
              </svg>
            ) : isNegative ? (
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 13l-5 5m0 0l-5-5m5 5V6"
                />
              </svg>
            ) : null}
            {Math.abs(change)}%
          </div>
        )}
      </div>
      <div>
        <h3 className="text-2xl font-bold text-[#1a1a1a]">{value}</h3>
        <p className="text-sm text-[#666] mt-1">{title}</p>
        {subtitle && <p className="text-xs text-[#999] mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const MetricsCards = ({ user }) => {
  const { data: metricsData, isLoading } = useQuery({
    queryFn: () => getDashboardOverviewMetrics(user?.organizationId),
    queryKey: ['dashboardOverviewMetrics', user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 30000,
    cacheTime: 60000,
  });

  const metrics = metricsData?.metrics || {};
  // console.log('metrics=>', metrics);

  const cards = [
    {
      title: 'Active Surveys',
      value: isLoading ? '-' : metrics.activeSurveys || 0,
      subtitle: `${metrics.totalSurveys || 0} total surveys`,
      icon: (
        <svg
          className="w-6 h-6 text-blue-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      color: 'bg-blue-50',
    },
    {
      title: 'Total Completes',
      value: isLoading ? '-' : (metrics.totalCompletes || 0).toLocaleString(),
      subtitle: `${metrics.thisMonthCompletes || 0} this month`,
      change: metrics.completesChange,
      icon: (
        <svg
          className="w-6 h-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      color: 'bg-green-50',
    },
    {
      title: 'Reconciled IDs',
      value: isLoading ? '-' : (metrics.reconciledIds || 0).toLocaleString(),
      subtitle: `${(metrics.pendingReconciliationIds || 0).toLocaleString()} pending`,
      icon: (
        <svg
          className="w-6 h-6 text-teal-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
      color: 'bg-teal-50',
    },
    {
      title: 'Conversion Rate',
      value: isLoading ? '-' : `${metrics.conversionRate || 0}%`,
      subtitle: `Avg CPI: $${(metrics.avgCpi || 0).toFixed(2)}`,
      icon: (
        <svg
          className="w-6 h-6 text-purple-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
      color: 'bg-purple-50',
    },
    {
      title: 'Pending Amount',
      value: isLoading
        ? '-'
        : `$${(metrics.pendingAmount || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
      subtitle: `${metrics.unpaidInvoices || 0} unpaid invoices`,
      icon: (
        <svg
          className="w-6 h-6 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      color: 'bg-red-50',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="bg-white border border-[#E0E0E0] rounded-xl p-5 animate-pulse"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-gray-200"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-24"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card, index) => (
        <MetricCard key={index} {...card} />
      ))}
    </div>
  );
};

export default MetricsCards;
